import { lazy, Suspense, useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { LmsPrintPreviewPdfButton } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import { memberApi, MemberRequest } from '@/api/b2b'
import type { GuardianInfo, GuardianRegisteredChild, Member } from '@/api/b2b'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import type { LearnerStatusCode } from '@/api/learnerStatus'
import { LearnerStatusSummarySection } from '@/components/admin/members/LearnerStatusSummarySection'
import { LeaveHistorySection } from '@/components/admin/members/LeaveHistorySection'
import { AttendanceHistoryModal } from '@/components/admin/members/AttendanceHistoryModal'
import { lcStudentApi, type LcStudentRow } from '@/api/lcStudents'
import { DiagnosticCell } from '@/components/admin/diagnostics/DiagnosticCell'
import { useDiagnosticSets } from '@/components/admin/diagnostics/useDiagnosticSets'
import { AdminPagination } from '@/components/admin/members/AdminPagination'
import { AssignModal } from '@/components/admin/diagnostics/AssignModal'
import { GradeModal } from '@/components/admin/diagnostics/GradeModal'
import { ResultModal } from '@/components/admin/diagnostics/ResultModal'
import { ResetModal } from '@/components/admin/diagnostics/ResetModal'
import { RetakeModal } from '@/components/admin/diagnostics/RetakeModal'
import { HistoryModal } from '@/components/admin/diagnostics/HistoryModal'
import { ModalShell } from '@/components/admin/diagnostics/ModalShell'
import { pickDiagnosticRep } from '@/components/admin/diagnostics/diagnosticRep'
import { useDiagnosticLiveResult } from '@/hooks/admin/useDiagnosticLiveResult'
import type { DiagnosticAssignmentRow } from '@/api/adminDiagnostics'
import { Plus, Search, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Building2, QrCode, Copy, Check } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import KakaoPostcode from '@/components/common/KakaoPostcode'
import BirthDatePicker from '@/components/common/BirthDatePicker'
import { LearnerHandleInput } from '@/components/common/LearnerHandleInput'
import { TERMS_VERSION } from '@/api/selfRegister'
import { validateLearnerHandle, buildLearnerEmail, learnerHandle } from '@/utils/learnerEmail'
import { deriveGradeKey } from '@/utils/gradeDerivation'
import { aiqSetName } from '@/utils/aiqSetName'
import { levelSetName } from '@/utils/levelSetName'
import { RowActionMenu } from '@/components/admin/members/RowActionMenu'
import { LearnerConsultationModal } from '@/components/admin/members/LearnerConsultationModal'
import { EnrollmentHistoryModal } from '@/components/admin/members/EnrollmentHistoryModal'
import { ReEnrollmentModal } from '@/components/admin/members/ReEnrollmentModal'
import { TestReportRecommendedCourse } from './TestReportRecommendedCourse'
import type { RowActionItem } from '@/components/admin/members/RowActionMenu'
import { computeTenure, formatTenure } from '@/utils/tenure'
import {
  getGradeLevelLabel,
  getGenderLabel,
} from '@/utils/studentLabels'

const ConsentDetailModal = lazy(() =>
  import('@/components/auth/ConsentDetailModal').then((module) => ({
    default: module.ConsentDetailModal,
  })),
)

interface StudentFormData {
  email: string
  fullName: string
  password?: string
  role: string
  gradeLevel?: string
  dateOfBirth?: string
  gender?: string
  school?: string
  guardian: GuardianInfo
}

type SortField = 'name' | 'gradeLevel' | 'createdAt'
type SortDirection = 'asc' | 'desc'
type StudentStatusFilter = LearnerStatusCode | 'ALL'

// 상태 필터 탭 순서 (재원 기본 → 휴원 → 예비 → 퇴원 → 전체)
const STATUS_FILTER_TABS: StudentStatusFilter[] = ['ENROLLED', 'ON_LEAVE', 'WAITING', 'WITHDRAWN', 'ALL']

/** Registered-date display as YY-MM-DD (deterministic: parse leading 10 chars of ISO). */
const formatRegisteredDate = (iso: string | null | undefined): string => {
  if (!iso) return '-'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return '-'
  return `${m[1].slice(2)}-${m[2]}-${m[3]}`
}

export default function StudentManagement() {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isRegistrationMethodOpen, setIsRegistrationMethodOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isParentQrOpen, setIsParentQrOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<number | null>(null)
  const [copiedParentRegisterUrl, setCopiedParentRegisterUrl] = useState(false)
  const [centerTermsAgreed, setCenterTermsAgreed] = useState(false)
  const [centerMarketingAgreed, setCenterMarketingAgreed] = useState(false)
  const [consentDetailKind, setConsentDetailKind] = useState<'terms' | 'marketing' | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const statusParam = searchParams.get('status')
  const statusFilter: StudentStatusFilter = statusParam && STATUS_FILTER_TABS.includes(statusParam as StudentStatusFilter)
    ? statusParam as StudentStatusFilter
    : 'ENROLLED'
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [page, setPage] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { user } = useAuthStore()
  const canManageDiagnostics = hasLcPermission(user ?? undefined, 'STUDENT')
  const canReEnroll = canManageDiagnostics && hasLcPermission(user ?? undefined, 'CLASS')
  const { ageSets, gradeSets } = useDiagnosticSets({ enabled: canManageDiagnostics })
  const [diagnosticPanelFor, setDiagnosticPanelFor] = useState<Member | null>(null)
  const [assignFor, setAssignFor] = useState<Member | null>(null)
  const [gradeFor, setGradeFor] = useState<DiagnosticAssignmentRow | null>(null)
  const [resultFor, setResultFor] = useState<{ assignment: DiagnosticAssignmentRow; name: string } | null>(null)
  const [resetFor, setResetFor] = useState<{ assignment: DiagnosticAssignmentRow; name: string } | null>(null)
  const [retakeFor, setRetakeFor] = useState<{ assignment: DiagnosticAssignmentRow; name: string } | null>(null)
  const [historyFor, setHistoryFor] = useState<{ rows: DiagnosticAssignmentRow[]; name: string } | null>(null)
  const [formData, setFormData] = useState<StudentFormData>({
    email: '',
    fullName: '',
    password: 's1234',
    role: 'member',
    gradeLevel: '',
    dateOfBirth: '',
    gender: '',
    school: '',
    guardian: {},
  })
  const [registeredChildren, setRegisteredChildren] = useState<GuardianRegisteredChild[]>([])

  // (c) debounce 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (!isModalOpen || editingMember) {
      setRegisteredChildren([])
      return
    }
    const phone = formData.guardian.phone?.trim() ?? ''
    const digits = phone.replace(/\D/g, '')
    if (digits.length < 10) {
      setRegisteredChildren([])
      return
    }

    let cancelled = false
    const timer = window.setTimeout(() => {
      memberApi.getChildrenByGuardianPhone(phone)
        .then((response) => {
          if (!cancelled) setRegisteredChildren(response.data.data ?? [])
        })
        .catch(() => {
          if (!cancelled) setRegisteredChildren([])
        })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [editingMember, formData.guardian.phone, isModalOpen])

  // (c) 검색/상태/정렬 변경 시 page 리셋
  useEffect(() => { setPage(0) }, [debouncedSearch, statusFilter, sortField, sortDirection])

  // (d) 서버 원생 목록 쿼리
  const { data: studentsData, isLoading, error: studentsError } = useQuery({
    queryKey: ['lc', 'students', { q: debouncedSearch, status: statusFilter, sort: sortField, dir: sortDirection, page }],
    queryFn: () => lcStudentApi.search({
      q: debouncedSearch,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      sort: sortField,
      dir: sortDirection,
      page,
      size: 20,
    }),
    retry: 1,
  })
  const pageData = studentsData?.data.data
  const rows: LcStudentRow[] = pageData?.content ?? []

  const totalsByStatus = pageData?.totalsByStatus ?? { WAITING: 0, ENROLLED: 0, ON_LEAVE: 0, WITHDRAWN: 0 }
  const totalStudents = Object.values(totalsByStatus).reduce((sum, count) => sum + count, 0)
  const totalPages = pageData?.totalPages ?? 0

  const [attendanceFor, setAttendanceFor] = useState<Member | null>(null)
  const [enrollmentHistoryFor, setEnrollmentHistoryFor] = useState<Member | null>(null)
  const [reEnrollFor, setReEnrollFor] = useState<Member | null>(null)
  const [consultationFor, setConsultationFor] = useState<Member | null>(null)

  const createMutation = useMutation({
    mutationFn: (data: MemberRequest) => memberApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['lc', 'students'] })
      alert(t('admin:students.toast.createSuccess'))
      closeModal()
    },
    onError: (error: any) => {
      console.error('Student create failed:', error)
      let errorMessage = t('admin:students.toast.createFailed')
      if (error?.response?.data?.error) {
        const errorObj = error.response.data.error
        if (typeof errorObj === 'string') {
          errorMessage = errorObj
        } else if (errorObj?.message) {
          errorMessage = errorObj.message
        }
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }
      alert(t('admin:students.toast.errorPrefix', { message: errorMessage }))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: any }) =>
      memberApi.update(userId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['members'] })
      await queryClient.invalidateQueries({ queryKey: ['lc', 'students'] })
      await queryClient.refetchQueries({ queryKey: ['lc', 'students'] })
      alert(t('admin:students.toast.updateSuccess'))
      closeModal()
    },
    onError: (error: any) => {
      console.error('Student update failed:', error)
      let errorMessage = t('admin:students.toast.updateFailed')
      if (error?.response?.data?.error) {
        const errorObj = error.response.data.error
        if (typeof errorObj === 'string') {
          errorMessage = errorObj
        } else if (errorObj?.message) {
          errorMessage = errorObj.message
        }
      }
      alert(t('admin:students.toast.errorPrefix', { message: errorMessage }))
    },
  })

  const refreshDiagnostics = async () => {
    await queryClient.invalidateQueries({ queryKey: ['lc', 'students'] })
    await queryClient.refetchQueries({ queryKey: ['lc', 'students'] })
  }

  const getDiagnosticSetName = (setId: number | undefined, kind: 'aiq' | 'level') => {
    if (setId === undefined) return '-'
    const sets = kind === 'aiq' ? ageSets : gradeSets
    const set = sets.find((candidate) => candidate.id === setId)
    if (!set) return t('admin:diagnostic.assignmentInfo.fallbackSetName', { id: setId })
    if (kind === 'level' && set.levelValue) {
      const name = levelSetName(set.levelValue, set.semester)
      return name ? t(`admin:diagnosticSets.${name.key}`, name.params) : set.name
    }
    if (set.levelValue) {
      const name = aiqSetName(set.levelValue)
      return name ? t(`admin:diagnosticSets.${name.key}`, name.params) : set.name
    }
    return set.name
  }

  const parentRegistrationUrl = useMemo(() => {
    if (typeof window === 'undefined') return '/self-register'
    return new URL('/self-register', window.location.origin).toString()
  }, [])
  const detailSearch = searchParams.toString()

  const setStatusFilter = (status: StudentStatusFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('status', status)
      return next
    }, { replace: true })
  }

  const studentDetailPath = (userId: number) =>
    detailSearch ? `/admin/students/${userId}?${detailSearch}` : `/admin/students/${userId}`

  const handleCopyParentRegistrationUrl = async () => {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(parentRegistrationUrl)
    }
    setCopiedParentRegisterUrl(true)
    window.setTimeout(() => setCopiedParentRegisterUrl(false), 1500)
  }

  // (b) handleSort — sortField은 항상 non-null
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-gray-400" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-primary-600" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-primary-600" />
  }

  const openCreateModal = () => {
    setIsRegistrationMethodOpen(false)
    setEditingMember(null)
    setCenterTermsAgreed(false)
    setCenterMarketingAgreed(false)
    setFormData({
      email: '',
      fullName: '',
      password: 's1234',
      role: 'member',
      gradeLevel: '',
      dateOfBirth: '',
      gender: '',
      school: '',
      guardian: {},
    })
    setRegisteredChildren([])
    setIsModalOpen(true)
  }

  const openParentRegistrationQr = () => {
    setIsRegistrationMethodOpen(false)
    setIsParentQrOpen(true)
  }

  const openEditModal = (member: Member) => {
    setEditingMember(member.userId)
    setCenterTermsAgreed(false)
    setCenterMarketingAgreed(false)
    setFormData({
      email: learnerHandle(member.email),
      fullName: member.fullName,
      role: member.role,
      gradeLevel: member.gradeLevel || '',
      dateOfBirth: member.dateOfBirth || '',
      gender: member.gender || '',
      school: member.school || '',
      guardian: member.guardian || {},
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingMember(null)
    setRegisteredChildren([])
    setCenterTermsAgreed(false)
    setCenterMarketingAgreed(false)
    setConsentDetailKind(null)
  }

  const editingMemberInfo = editingMember ? rows.find((r) => r.member.userId === editingMember)?.member : null
  const isEmailChanged = editingMemberInfo && buildLearnerEmail(formData.email) !== editingMemberInfo.email

  const handleErrorCode = validateLearnerHandle(formData.email)
  const handleError =
    formData.email && handleErrorCode === 'contains_at'
      ? t('admin:students.form.handleErrorAt')
      : formData.email && handleErrorCode === 'invalid_char'
        ? t('admin:students.form.handleErrorChar')
        : undefined
  const centerAllAgreed = centerTermsAgreed && centerMarketingAgreed

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const handleCode = validateLearnerHandle(formData.email)
    if (handleCode !== 'ok') {
      alert(
        handleCode === 'contains_at'
          ? t('admin:students.form.handleErrorAt')
          : handleCode === 'invalid_char'
            ? t('admin:students.form.handleErrorChar')
            : t('admin:students.form.handleErrorEmpty')
      )
      return
    }
    if (!editingMember && !centerTermsAgreed) {
      alert(t('admin:students.consent.requiredAlert'))
      return
    }
    const guardian = formData.guardian
    const hasGuardian = !!(guardian.name || guardian.phone || guardian.postalCode || guardian.addressBase || guardian.addressDetail)
    const derivedGradeLevel = deriveGradeKey(formData.dateOfBirth || '')
    const profile = {
      dateOfBirth: formData.dateOfBirth?.trim() || undefined,
      gender: formData.gender || undefined,
      school: formData.school?.trim() || undefined,
      guardian: hasGuardian ? guardian : undefined,
    }
    if (editingMember) {
      updateMutation.mutate({
        userId: editingMember,
        data: {
          role: formData.role,
          gradeLevel: derivedGradeLevel || undefined,
          fullName: formData.fullName?.trim() || undefined,
          email: buildLearnerEmail(formData.email),
          ...profile,
        },
      })
    } else {
      const submitData: MemberRequest = {
        email: buildLearnerEmail(formData.email),
        fullName: formData.fullName.trim(),
        password: formData.password?.trim() || 's1234',
        role: formData.role || 'member',
        gradeLevel: derivedGradeLevel || undefined,
        consent: {
          termsAgreed: centerTermsAgreed,
          marketingAgreed: centerMarketingAgreed,
          termsVersion: TERMS_VERSION,
        },
        ...profile,
      }
      createMutation.mutate(submitData)
    }
  }

  const diagnosticPanelRows = diagnosticPanelFor
    ? rows.find((row) => row.member.userId === diagnosticPanelFor.userId)?.diagnostic?.rows ?? []
    : []
  const diagnosticPanelBaseAssignment = pickDiagnosticRep(diagnosticPanelRows)
  const { assignment: diagnosticPanelAssignment } = useDiagnosticLiveResult({
    assignment: diagnosticPanelBaseAssignment,
    userId: diagnosticPanelFor?.userId,
    enabled: diagnosticPanelFor != null,
  })
  const diagnosticPanelLiveRows = diagnosticPanelAssignment
    ? diagnosticPanelRows.map((row) =>
        row.id === diagnosticPanelAssignment.id ? diagnosticPanelAssignment : row,
      )
    : diagnosticPanelRows
  const diagnosticPanelHasReport = diagnosticPanelLiveRows.some((row) => row.status === 'COMPLETED')

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin:students.title')}</h1>
          <p className="mt-1 text-gray-500">{t('admin:students.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsRegistrationMethodOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5" />
            <span>{t('admin:students.actions.create')}</span>
          </button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('admin:students.table.memberStatus')}>
        {STATUS_FILTER_TABS.map((code) => {
          const active = statusFilter === code
          const count = code === 'ALL' ? totalStudents : totalsByStatus[code]
          return (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStatusFilter(code)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                active
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {t(`admin:students.filter.${code}`)}
              <span className={`ml-1.5 ${active ? 'text-white/80' : 'text-gray-400'}`}>{count}</span>
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('admin:students.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Student List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('common:label.loading')}</div>
        ) : studentsError ? (
          <div className="p-8 text-center">
            <div className="text-red-600 mb-2">{t('admin:students.error.loadFailed')}</div>
            <div className="text-sm text-gray-500">
              {studentsError instanceof Error ? studentsError.message : t('admin:students.error.unknown')}
            </div>
            <button
              onClick={() => queryClient.refetchQueries({ queryKey: ['lc', 'students'] })}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {t('admin:students.actions.retry')}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {debouncedSearch || statusFilter !== 'ENROLLED' ? t('common:table.noResults') : t('admin:students.empty.title')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1240px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                    onClick={() => handleSort('name')}
                  >
                    <span className="flex items-center">
                      {t('admin:students.table.studentName')}
                      {renderSortIcon('name')}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.handle')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                    onClick={() => handleSort('gradeLevel')}
                  >
                    <span className="flex items-center">
                      {t('admin:students.table.gradeLevel')}
                      {renderSortIcon('gradeLevel')}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.gender')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.guardianPhone')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.courseName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.className')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:diagnostic.columnTitle')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('createdAt')}
                  >
                    <span className="flex items-center">
                      {t('admin:students.table.registeredAt')}
                      {renderSortIcon('createdAt')}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.tenure')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {t('admin:students.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row) => {
                  const member = row.member
                  const tenure = computeTenure(member.createdAt)
                  const diagnosticRows = row.diagnostic?.rows ?? []
                  // 미취학·생년월일 없음은 서버가 eligible=false로 내려준다 → 진단 메뉴·회차 전체 숨김
                  const diagnosticIneligibleReason = row.diagnostic?.eligible === false ? row.diagnostic.ineligibleReason : null
                  const items: RowActionItem[] = [
                    { key: 'edit', label: t('admin:students.rowMenu.edit'), onClick: () => openEditModal(member) },
                    ...(canManageDiagnostics && !diagnosticIneligibleReason
                      ? [
                          { key: 'diagnosticManage', label: t('admin:students.rowMenu.diagnosticTest'), onClick: () => setDiagnosticPanelFor(member) },
                        ]
                      : []),
                    { key: 'attendance', label: t('admin:students.rowMenu.attendance'), onClick: () => setAttendanceFor(member) },
                    { key: 'enrollmentHistory', label: t('admin:students.rowMenu.enrollmentHistory'), onClick: () => setEnrollmentHistoryFor(member) },
                    { key: 'consultation', label: t('admin:students.rowMenu.consultation'), onClick: () => setConsultationFor(member) },
                    ...(row.derivedStatus === 'WITHDRAWN' && canReEnroll
                      ? [
                          { key: 'reEnroll', label: t('admin:students.rowMenu.reEnroll'), onClick: () => setReEnrollFor(member) },
                        ]
                      : []),
                  ]
                  return (
                    <tr key={member.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link
                          to={studentDetailPath(member.userId)}
                          className="font-medium text-gray-900 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-sm"
                        >
                          {member.fullName}
                        </Link>
                        {member.role === 'admin' && (
                          <span className="ml-2 inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">{t('admin:students.badge.admin')}</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {learnerHandle(member.email)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {getGradeLevelLabel(member.gradeLevel, t)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {getGenderLabel(member.gender, t)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {member.guardian?.phone ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {row.courseName ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {row.classNames?.join(', ') ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {diagnosticIneligibleReason ? (
                          <span data-testid="diag-ineligible" className="text-sm text-gray-400">
                            {t(`admin:diagnostic.ineligible.${diagnosticIneligibleReason}`)}
                          </span>
                        ) : (
                          <DiagnosticCell
                            assignment={pickDiagnosticRep(diagnosticRows)}
                            canManage={false}
                            historyCount={diagnosticRows.length}
                            onAssign={() => {}}
                            onGrade={() => {}}
                            onResult={() => {}}
                            onReset={() => {}}
                          />
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatRegisteredDate(member.createdAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {tenure
                          ? formatTenure(tenure, {
                              years: t('admin:students.tenure.units.years'),
                              months: t('admin:students.tenure.units.months'),
                              days: t('admin:students.tenure.units.days'),
                            })
                          : t('admin:students.tenure.none')}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex justify-end">
                          <RowActionMenu triggerLabel={t('admin:students.rowMenu.trigger')} items={items} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* (j) 페이지네이션 */}
      <AdminPagination page={page} totalPages={totalPages} onChange={setPage} />

      {/* Registration method chooser */}
      <Dialog open={isRegistrationMethodOpen} onOpenChange={setIsRegistrationMethodOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b pr-10">
            <DialogTitle>{t('admin:students.registrationMethod.title')}</DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-3">
            <button
              type="button"
              onClick={openCreateModal}
              className="w-full flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-primary-300 hover:bg-primary-50/40 transition-colors"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                <Building2 className="size-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900">
                  {t('admin:students.registrationMethod.centerTitle')}
                </span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  {t('admin:students.registrationMethod.centerDescription')}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={openParentRegistrationQr}
              className="w-full flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-primary-300 hover:bg-primary-50/40 transition-colors"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-700">
                <QrCode className="size-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900">
                  {t('admin:students.registrationMethod.parentTitle')}
                </span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  {t('admin:students.registrationMethod.parentDescription')}
                </span>
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Parent self-registration QR */}
      <Dialog open={isParentQrOpen} onOpenChange={setIsParentQrOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b pr-10">
            <DialogTitle>{t('admin:students.parentQr.title')}</DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-5">
            <div className="flex justify-center">
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <QRCodeSVG
                  value={parentRegistrationUrl}
                  size={196}
                  level="M"
                  aria-label={t('admin:students.parentQr.qrAlt')}
                />
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">
                {t('admin:students.parentQr.urlLabel')}
              </p>
              <p className="break-all text-sm text-gray-800">{parentRegistrationUrl}</p>
            </div>
            <p className="text-xs leading-5 text-gray-500">
              {t('admin:students.parentQr.description')}
            </p>
          </div>
          <DialogFooter className="mx-0 mb-0 px-5">
            <button
              type="button"
              onClick={() => setIsParentQrOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common:button.close')}
            </button>
            <button
              type="button"
              onClick={() => void handleCopyParentRegistrationUrl()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {copiedParentRegisterUrl ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copiedParentRegisterUrl ? t('admin:students.parentQr.copied') : t('admin:students.parentQr.copyUrl')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) closeModal() }}>
        <DialogContent className="sm:max-w-md p-0 gap-0 grid-rows-[auto_minmax(0,1fr)_auto]">
          <DialogHeader className="px-5 py-4 border-b pr-10">
            <DialogTitle>
              <div>
                <span>
                  {editingMember
                    ? t('admin:students.modal.editTitle', { name: editingMemberInfo?.fullName ?? '' })
                    : t('admin:students.modal.createTitle')}
                </span>
                {editingMember && (
                  <p className="text-sm font-normal text-gray-500 mt-0.5">{learnerHandle(editingMemberInfo?.email)}</p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
        <form id="student-form" onSubmit={handleSubmit} className="space-y-4 px-5 py-4 overflow-y-auto">
          {editingMember && <LearnerStatusSummarySection userId={editingMember} />}
          {editingMember && <LeaveHistorySection userId={editingMember} />}
          {!editingMember && (
            <>
              <h4 className="text-sm font-semibold text-gray-800">{t('admin:students.form.childInfoSection')}</h4>
              <div>
                <label htmlFor="student-handle" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin:students.form.childHandle')} *
                </label>
                <LearnerHandleInput
                  id="student-handle"
                  required
                  value={formData.email}
                  onChange={(v) => setFormData({ ...formData, email: v })}
                  error={handleError}
                />
              </div>

              <div>
                <label htmlFor="student-name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin:students.form.childName')} *
                </label>
                <input
                  id="student-name"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder={t('admin:students.form.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="student-initial-password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin:students.form.initialPassword')}
                </label>
                <input
                  id="student-initial-password"
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="s1234"
                />
                <p className="mt-1 text-xs text-gray-500">{t('admin:students.form.initialPasswordHelp')}</p>
              </div>
            </>
          )}

          {editingMember && (
            <>
              <div>
                <label htmlFor="student-name-edit" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin:students.form.name')}
                </label>
                <input
                  id="student-name-edit"
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder={t('admin:students.form.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="student-handle-edit" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin:students.form.handle')}
                </label>
                <LearnerHandleInput
                  id="student-handle-edit"
                  value={formData.email}
                  onChange={(v) => setFormData({ ...formData, email: v })}
                  warning={!!isEmailChanged}
                  error={handleError}
                />
                {isEmailChanged && (
                  <div className="mt-2 flex items-start space-x-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      <Trans
                        i18nKey="students.form.emailChangeWarning"
                        ns="admin"
                        components={{ strong: <strong /> }}
                      />
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <label htmlFor="student-date-of-birth" className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.form.dateOfBirth')} *
            </label>
            <BirthDatePicker
              id="student-date-of-birth"
              required
              value={formData.dateOfBirth || ''}
              onChange={(dateOfBirth) => setFormData((prev) => ({ ...prev, dateOfBirth }))}
              inputClassName="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="student-gender" className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.form.gender')}
            </label>
            <select
              id="student-gender"
              value={formData.gender || ''}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('admin:students.form.genderNone')}</option>
              <option value="male">{t('admin:students.form.genderMale')}</option>
              <option value="female">{t('admin:students.form.genderFemale')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="student-school" className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.form.school')}
            </label>
            <input
              id="student-school"
              type="text"
              value={formData.school || ''}
              onChange={(e) => setFormData({ ...formData, school: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder={t('admin:students.form.schoolPlaceholder')}
            />
          </div>

          <div className="border-t pt-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-800">{t('admin:students.guardian.section')}</h4>
            <div>
              <label htmlFor="guardian-name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:students.guardian.name')} *
              </label>
              <input
                id="guardian-name"
                type="text"
                required
                value={formData.guardian.name || ''}
                onChange={(e) => setFormData({ ...formData, guardian: { ...formData.guardian, name: e.target.value } })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="guardian-phone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:students.guardian.phone')} *
              </label>
              <input
                id="guardian-phone"
                type="tel"
                required
                value={formData.guardian.phone || ''}
                onChange={(e) => setFormData({ ...formData, guardian: { ...formData.guardian, phone: e.target.value } })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="010-1234-5678"
              />
            </div>
            {!editingMember && registeredChildren.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900 mb-2">이미 등록된 자녀</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-amber-800">
                        <th className="py-1 pr-3 font-medium">이름</th>
                        <th className="py-1 pr-3 font-medium">아이디</th>
                        <th className="py-1 pr-3 font-medium">생년월일</th>
                        <th className="py-1 font-medium">학년</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registeredChildren.map((child) => (
                        <tr key={`${child.handle}-${child.dateOfBirth ?? ''}`} className="text-gray-800">
                          <td className="py-1 pr-3">{child.studentName}</td>
                          <td className="py-1 pr-3">{child.handle}</td>
                          <td className="py-1 pr-3">{child.dateOfBirth ?? '-'}</td>
                          <td className="py-1">{getGradeLevelLabel(child.gradeLabel, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div>
              <label htmlFor="guardian-postal-code" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:students.guardian.postalCode')}
              </label>
              <div className="flex gap-2">
                <input
                  id="guardian-postal-code"
                  type="text"
                  readOnly
                  value={formData.guardian.postalCode || ''}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  placeholder={t('admin:students.guardian.postalCode')}
                />
                <KakaoPostcode
                  label={t('admin:students.guardian.searchAddress')}
                  onSelect={({ postalCode, addressBase }) =>
                    setFormData((prev) => ({
                      ...prev,
                      guardian: { ...prev.guardian, postalCode, addressBase },
                    }))
                  }
                />
              </div>
            </div>
            <div>
              <label htmlFor="guardian-address-base" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:students.guardian.addressBase')} *
              </label>
              <input
                id="guardian-address-base"
                type="text"
                required
                value={formData.guardian.addressBase || ''}
                onChange={(e) => setFormData({ ...formData, guardian: { ...formData.guardian, addressBase: e.target.value } })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
            <div>
              <label htmlFor="guardian-address-detail" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:students.guardian.addressDetail')}
              </label>
              <input
                id="guardian-address-detail"
                type="text"
                value={formData.guardian.addressDetail || ''}
                onChange={(e) => setFormData({ ...formData, guardian: { ...formData.guardian, addressDetail: e.target.value } })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {!editingMember && (
            <div className="border-t pt-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-800">{t('admin:students.consent.section')}</h4>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <input
                  type="checkbox"
                  checked={centerAllAgreed}
                  onChange={(e) => {
                    setCenterTermsAgreed(e.target.checked)
                    setCenterMarketingAgreed(e.target.checked)
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {t('admin:students.consent.agreeAll')}
              </label>
              <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <ConsentCheckboxRow
                  label={t('admin:students.consent.agreeTerms')}
                  tag={t('admin:students.consent.requiredTag')}
                  checked={centerTermsAgreed}
                  onChange={setCenterTermsAgreed}
                  onDetail={() => setConsentDetailKind('terms')}
                  detailLabel={t('admin:students.consent.viewDetail')}
                />
                <ConsentCheckboxRow
                  label={t('admin:students.consent.agreeMarketing')}
                  tag={t('admin:students.consent.optionalTag')}
                  checked={centerMarketingAgreed}
                  onChange={setCenterMarketingAgreed}
                  onDetail={() => setConsentDetailKind('marketing')}
                  detailLabel={t('admin:students.consent.viewDetail')}
                />
              </div>
              <p className="text-xs leading-5 text-gray-500">
                {t('admin:students.consent.centerNotice')}
              </p>
            </div>
          )}
        </form>
          <DialogFooter className="mx-0 mb-0 px-5">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              form="student-form"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {editingMember ? t('admin:students.form.submitUpdate') : t('admin:students.form.submitCreate')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {consentDetailKind != null ? (
        <Suspense fallback={null}>
          <ConsentDetailModal
            open
            kind={consentDetailKind}
            onClose={() => setConsentDetailKind(null)}
          />
        </Suspense>
      ) : null}

      {/* 수업 기록(출결 이력) 조회 모달 — 2026-06-15 */}
      {attendanceFor && (
        <AttendanceHistoryModal
          userId={attendanceFor.userId}
          learnerName={attendanceFor.fullName}
          onClose={() => setAttendanceFor(null)}
        />
      )}

      {consultationFor && (
        <LearnerConsultationModal
          userId={consultationFor.userId}
          learnerName={consultationFor.fullName}
          onClose={() => setConsultationFor(null)}
        />
      )}

      {/* 멀티센터 재원 이력 팝업 — 1차 UI(backend 추후), placeholder 데이터 */}
      {enrollmentHistoryFor && (
        <EnrollmentHistoryModal
          learnerName={enrollmentHistoryFor.fullName}
          onClose={() => setEnrollmentHistoryFor(null)}
        />
      )}

      {/* Diagnostic (진단 테스트) Modals */}
      {diagnosticPanelFor && (
        <ModalShell
          title={`${diagnosticPanelFor.fullName} (${learnerHandle(diagnosticPanelFor.email)}) — ${t('admin:diagnostic.columnTitle')}`}
          onClose={() => setDiagnosticPanelFor(null)}
        >
          <div className="space-y-4 rounded-lg border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
            <DiagnosticCell
              assignment={diagnosticPanelAssignment}
              canManage={canManageDiagnostics}
              historyCount={diagnosticPanelLiveRows.length}
              onAssign={() => setAssignFor(diagnosticPanelFor)}
              onGrade={setGradeFor}
              onResult={(assignment) => setResultFor({ assignment, name: diagnosticPanelFor.fullName })}
              onReset={(assignment) => setResetFor({ assignment, name: diagnosticPanelFor.fullName })}
              onRetake={(assignment) => setRetakeFor({ assignment, name: diagnosticPanelFor.fullName })}
              onHistory={() => setHistoryFor({ rows: diagnosticPanelLiveRows, name: diagnosticPanelFor.fullName })}
            />
            {diagnosticPanelAssignment && (
              <div className="grid gap-3 border-t border-slate-200 pt-4 text-sm sm:grid-cols-2">
                <div className="rounded-md border border-white bg-white/80 px-3 py-2 shadow-sm">
                  <p className="text-xs font-semibold text-slate-500">{t('admin:diagnostic.assignmentInfo.aiqSet')}</p>
                  <p className="mt-1 font-semibold text-slate-950">
                    {getDiagnosticSetName(diagnosticPanelAssignment.aiqQuizSetId, 'aiq')}
                  </p>
                </div>
                <div className="rounded-md border border-white bg-white/80 px-3 py-2 shadow-sm">
                  <p className="text-xs font-semibold text-slate-500">{t('admin:diagnostic.assignmentInfo.levelSet')}</p>
                  <p className="mt-1 font-semibold text-slate-950">
                    {getDiagnosticSetName(diagnosticPanelAssignment.levelQuizSetId, 'level')}
                  </p>
                </div>
              </div>
            )}
            <TestReportRecommendedCourse learnerId={diagnosticPanelFor.userId} />
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            {diagnosticPanelAssignment && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const learnerName = encodeURIComponent(diagnosticPanelFor.fullName)
                    window.open(
                      `/admin/coaching/print/${diagnosticPanelFor.userId}/${diagnosticPanelAssignment.levelQuizSetId}/diagnostic-level?learnerName=${learnerName}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }}
                  className="px-4 py-2 rounded-lg border border-primary-200 text-primary-700 bg-primary-50 text-sm hover:bg-primary-100"
                >
                  {t('admin:diagnostic.actions.printLevelTest')}
                </button>
                <LmsPrintPreviewPdfButton
                  href={`/admin/coaching/print/${diagnosticPanelFor.userId}/${diagnosticPanelAssignment.levelQuizSetId}/diagnostic-level?learnerName=${encodeURIComponent(diagnosticPanelFor.fullName)}`}
                />
              </>
            )}
            {diagnosticPanelHasReport && (
              <button
                type="button"
                onClick={() => {
                  window.open(
                    `/admin/students/${diagnosticPanelFor.userId}/diagnostic-report`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                  setDiagnosticPanelFor(null)
                }}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700"
              >
                {t('admin:diagnostic.actions.viewReport')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setDiagnosticPanelFor(null)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
            >
              {t('common:button.close')}
            </button>
          </div>
        </ModalShell>
      )}
      {assignFor && (
        <AssignModal
          student={assignFor}
          ageSets={ageSets}
          gradeSets={gradeSets}
          onClose={() => setAssignFor(null)}
          onDone={() => {
            setAssignFor(null)
            void refreshDiagnostics()
          }}
        />
      )}
      {gradeFor && (
        <GradeModal
          assignment={gradeFor}
          onClose={() => setGradeFor(null)}
          onDone={() => {
            setGradeFor(null)
            void refreshDiagnostics()
          }}
        />
      )}
      {resultFor && (
        <ResultModal
          assignmentId={resultFor.assignment.id}
          studentName={resultFor.name}
          onClose={() => setResultFor(null)}
        />
      )}
      {resetFor && (
        <ResetModal
          assignment={resetFor.assignment}
          studentName={resetFor.name}
          onClose={() => setResetFor(null)}
          onDone={() => {
            setResetFor(null)
            setDiagnosticPanelFor(null)
            refreshDiagnostics()
          }}
        />
      )}
      {retakeFor && (
        <RetakeModal
          source={retakeFor.assignment}
          studentName={retakeFor.name}
          ageSets={ageSets}
          gradeSets={gradeSets}
          onClose={() => setRetakeFor(null)}
          onDone={() => {
            setRetakeFor(null)
            setDiagnosticPanelFor(null)
            refreshDiagnostics()
          }}
        />
      )}
      {historyFor && (
        <HistoryModal
          history={historyFor.rows}
          studentName={historyFor.name}
          onClose={() => setHistoryFor(null)}
          onViewResult={(assignment) => setResultFor({ assignment, name: historyFor.name })}
        />
      )}
      {reEnrollFor && (
        <ReEnrollmentModal
          learner={{ userId: reEnrollFor.userId, fullName: reEnrollFor.fullName }}
          onClose={() => setReEnrollFor(null)}
        />
      )}
    </div>
  )
}

interface ConsentCheckboxRowProps {
  label: string
  tag: string
  checked: boolean
  onChange: (checked: boolean) => void
  onDetail: () => void
  detailLabel: string
}

function ConsentCheckboxRow({
  label,
  tag,
  checked,
  onChange,
  onDetail,
  detailLabel,
}: ConsentCheckboxRowProps) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="flex min-w-0 items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="min-w-0">
          <span>{label}</span>
          <span className="ml-1 text-xs text-gray-500">{tag}</span>
        </span>
      </label>
      <button
        type="button"
        onClick={onDetail}
        className="shrink-0 text-xs font-medium text-primary-700 hover:underline"
      >
        {detailLabel}
      </button>
    </div>
  )
}
